import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  signInWithEmail,
  signUpWithEmail,
  signOut,
  resetPassword,
  sendMagicLink,
  signInWithGoogle,
  getSession,
  type User as SupabaseUser
} from '@/lib/supabase/auth';
import { supabase } from '@/lib/supabase/client';
import type { AuthStore, User, AccessRequestData } from './types';
import { api } from '@/lib/api/client';
import { reportClientError } from '@/lib/api/error-reporter';
import type { AuthMeData, AuthRoleData } from '@/lib/shared-types/api/endpoints/auth';
import { extractRoleFromJWT, isJWTExpired } from '@/lib/auth/jwt-utils';
import { resendVerificationEmail as resendVerificationEmailApi } from '@/lib/api/auth-emails';
import { ROLE_NAMES, type RoleName, getRoleId } from '@/lib/constants/roles';

// Helper to map Supabase user + JWT claims to our App User type
const mapSupabaseUserWithJWT = (sbUser: SupabaseUser, token: string): User => {
  // Extract role from JWT token instead of defaulting
  const role = extractRoleFromJWT(token);

  return {
    id: 0, // Fallback ID, should be synced with backend
    email: sbUser.email || '',
    name: sbUser.user_metadata?.name || sbUser.email?.split('@')[0] || 'User',
    role,
    profilePictureUrl: sbUser.user_metadata?.avatar_url || null,
    status: 'active',
    createdAt: sbUser.created_at,
    updatedAt: sbUser.updated_at || new Date().toISOString(),
    emailVerified: !!sbUser.email_confirmed_at,
    authProvider: (sbUser.app_metadata?.provider as 'email' | 'google') || 'email'
  };
};

const ROLE_ID_BY_NAME: Record<RoleName, number> = {
  [ROLE_NAMES.MEMBER]: 1,
  [ROLE_NAMES.PASTOR]: 2,
  [ROLE_NAMES.MEDIA_TEAM]: 3,
  [ROLE_NAMES.ADMIN]: 4,
  [ROLE_NAMES.MODERATOR]: 5,
};

const isValidRoleName = (role: unknown): role is RoleName => {
  return typeof role === 'string' && Object.values(ROLE_NAMES).includes(role as RoleName);
};

const extractRoleNameFromMeResponse = (responseData: unknown): RoleName | undefined => {
  if (!responseData || typeof responseData !== 'object') return undefined;

  const data = responseData as {
    role?: unknown;
    user?: {
      role?: {
        name?: unknown;
      } | unknown;
    };
  };

  const directRole = data.role;
  if (isValidRoleName(directRole)) return directRole;

  const userRole = data.user?.role;
  if (isValidRoleName(userRole)) return userRole;

  const nestedRoleName = (userRole as { name?: unknown } | undefined)?.name;
  if (isValidRoleName(nestedRoleName)) return nestedRoleName;

  return undefined;
};

const resolveRoleWithBackendFallback = async (token: string, initialRole: User['role']): Promise<User['role']> => {
  // Single source of truth: backend DB role via /api/users/me.
  // JWT role is only used as a fallback when backend resolution fails.
  // 5-second timeout guards against hung network requests blocking auth initialization.
  if (api.isBackendUnavailable()) {
    return initialRole;
  }

  try {
    const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000));
    const fetchRole = api.get('/api/users/me');
    const response = await Promise.race([fetchRole, timeout]);

    if (response) {
      const backendRole = extractRoleNameFromMeResponse((response as Awaited<typeof fetchRole>).data);
      if ((response as Awaited<typeof fetchRole>).success && backendRole) {
        return {
          name: backendRole,
          id: ROLE_ID_BY_NAME[backendRole],
        };
      }
    } else {
      console.warn('[Auth Store] Backend role resolution timed out, using JWT role fallback');
    }
  } catch (error) {
    console.warn('[Auth Store] Backend role resolution failed, using JWT role fallback:', error);
  }

  return initialRole;
};

// Legacy helper for backwards compatibility during migration
const mapSupabaseUser = (sbUser: SupabaseUser): User => {
  return {
    id: 0, // Fallback ID, should be synced with backend
    email: sbUser.email || '',
    name: sbUser.user_metadata?.name || sbUser.email?.split('@')[0] || 'User',
    role: { id: 1, name: 'member' }, // Default fallback
    profilePictureUrl: sbUser.user_metadata?.avatar_url || null,
    status: 'active',
    createdAt: sbUser.created_at,
    updatedAt: sbUser.updated_at || new Date().toISOString(),
    emailVerified: !!sbUser.email_confirmed_at,
    authProvider: (sbUser.app_metadata?.provider as 'email' | 'google') || 'email'
  };
};

// Track auth subscription
let authSubscription: { unsubscribe: () => void } | null = null;
let initializeAuthInFlight: Promise<void> | null = null;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get): AuthStore => ({
      // Initial state
      user: null,
      isLoading: false,
      isInitialized: false,
      isAuthenticated: false,
      error: null,
      token: null,

      // State setters
      setUser: (user: User | null, token?: string | null) => {
        set({
          user,
          token: token !== undefined ? token : get().token,
          isAuthenticated: !!user,
          error: null
        });
      },

      setToken: (token: string | null) => {
        // SECURITY: Token is now stored in httpOnly cookie set by backend
        // Do NOT store tokens in localStorage (vulnerable to XSS attacks)
        // The browser automatically includes httpOnly cookies in requests via 'credentials: include'
        // This method is kept for backwards compatibility but does not store the token
        set({ token: token || null });

        // Clean up any legacy localStorage tokens from older sessions
        if (typeof window !== 'undefined' && localStorage.getItem('auth-token')) {
          try {
            localStorage.removeItem('auth-token');
            console.log('[Auth Store] Removed legacy localStorage token for security');
          } catch (e) {
            console.warn('[Auth Store] Could not clean up legacy token:', e);
          }
        }
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      setError: (error: string | null) => {
        set({ error, isLoading: false });
      },

      clearError: () => {
        set({ error: null });
      },

      // Auth Actions

      loginWithEmail: async (email: string, password: string) => {
        try {
          set({ isLoading: true, error: null });

          const { result, error } = await signInWithEmail(email, password);
          if (error) throw error;
          if (!result.user || !result.session) throw new Error('Authentication failed');

          const idToken = result.session.access_token;
          const jwtUser = mapSupabaseUserWithJWT(result.user, idToken);
          const resolvedRole = await resolveRoleWithBackendFallback(idToken, jwtUser.role);
          const user = { ...jwtUser, role: resolvedRole };

          set({
            user,
            token: idToken,
            isAuthenticated: true,
            isInitialized: true,
            isLoading: false,
            error: null
          });

          get().setToken(idToken);

          return { success: true, user };

        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : 'Login failed';
          reportClientError({
            message: errorMessage,
            errorName: error instanceof Error ? error.name : 'AuthError',
            stack: error instanceof Error ? error.stack : undefined,
            screen: 'login',
            code: 'AUTH_LOGIN_FAILED'
          });
          set({
            error: errorMessage,
            isLoading: false,
            user: null,
            isAuthenticated: false,
            token: null
          });
          get().setToken(null);
          return { success: false, error: errorMessage };
        }
      },

      loginWithGoogle: async () => {
        try {
          set({ isLoading: true, error: null });
          await signInWithGoogle();
          // Redirect happens automatically
          return { success: true };
        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : 'Google login failed';
          reportClientError({
            message: errorMessage,
            errorName: error instanceof Error ? error.name : 'AuthError',
            stack: error instanceof Error ? error.stack : undefined,
            screen: 'login',
            code: 'AUTH_GOOGLE_LOGIN_FAILED'
          });
          set({
            error: errorMessage,
            isLoading: false
          });
          return { success: false, error: errorMessage };
        }
      },

      signUpWithEmail: async (email: string, password: string, name: string) => {
        try {
          set({ isLoading: true, error: null });

          const { result, error } = await signUpWithEmail(email, password, name);
          if (error) throw error;
          if (!result.user) throw new Error('Registration failed');

          // If session exists (auto-signin), set it
          if (result.session) {
            const idToken = result.session.access_token;
            const jwtUser = mapSupabaseUserWithJWT(result.user, idToken);
            const resolvedRole = await resolveRoleWithBackendFallback(idToken, jwtUser.role);
            const user = { ...jwtUser, role: resolvedRole };

            set({
              user,
              token: idToken,
              isAuthenticated: true,
              isInitialized: true,
              isLoading: false,
              error: null
            });
            get().setToken(idToken);
            return { success: true, user };
          } else {
            // Verification email sent
            set({ isLoading: false });
            return { success: true, requiresVerification: true };
          }

        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : 'Sign up failed';
          reportClientError({
            message: errorMessage,
            errorName: error instanceof Error ? error.name : 'AuthError',
            stack: error instanceof Error ? error.stack : undefined,
            screen: 'signup',
            code: 'AUTH_SIGNUP_FAILED'
          });
          set({
            error: errorMessage,
            isLoading: false,
            user: null,
            isAuthenticated: false,
            token: null
          });
          return { success: false, error: errorMessage };
        }
      },

      logout: async () => {
        const startTime = Date.now();
        try {
          console.log('[Auth Store] Starting logout process...');
          set({ isLoading: true });

          // Clear localStorage auth tokens
          if (typeof window !== 'undefined') {
            try {
              // Clear legacy auth token
              if (localStorage.getItem('auth-token')) {
                localStorage.removeItem('auth-token');
              }
              // Clear Supabase session data
              const supabaseKeys = Object.keys(localStorage).filter(
                k => k.startsWith('sb-') && k.endsWith('-auth-token')
              );
              supabaseKeys.forEach(key => localStorage.removeItem(key));
              console.log('[Auth Store] Cleared localStorage auth data');
            } catch (e) {
              console.warn('[Auth Store] Could not clear localStorage:', e);
            }
          }

          // Force clear local state first
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isInitialized: true,
            isLoading: false,
            error: null
          });
          get().setToken(null);

          // Then call Supabase signOut (clears server-side session)
          await signOut();
          console.log('[Auth Store] Supabase signOut completed successfully');

          console.log('[Auth Store] Logout completed', {
            duration_ms: Date.now() - startTime,
            timestamp: new Date().toISOString()
          });

          return { success: true };
        } catch (error: unknown) {
          console.error('[Auth Store] Logout failed:', error);
          // Even if signOut fails, we've already cleared local state
          const errorMessage = error instanceof Error ? error.message : 'Logout failed';
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isInitialized: true,
            isLoading: false,
            error: errorMessage
          });
          get().setToken(null);
          return { success: false, error: errorMessage };
        }
      },

      resetPassword: async (email: string) => {
        try {
          set({ isLoading: true, error: null });
          await resetPassword(email);
          set({ isLoading: false });
          return { success: true };
        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : 'Password reset failed';
          reportClientError({
            message: errorMessage,
            errorName: error instanceof Error ? error.name : 'AuthError',
            stack: error instanceof Error ? error.stack : undefined,
            screen: 'forgot_password',
            code: 'AUTH_PASSWORD_RESET_FAILED'
          });
          set({ error: errorMessage, isLoading: false });
          return { success: false, error: errorMessage };
        }
      },

      resendVerificationEmail: async () => {
        try {
          set({ isLoading: true, error: null });
          await resendVerificationEmailApi();

          set({ isLoading: false });
          return { success: true };
        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to resend verification email';
          reportClientError({
            message: errorMessage,
            errorName: error instanceof Error ? error.name : 'AuthError',
            stack: error instanceof Error ? error.stack : undefined,
            screen: 'signup',
            code: 'AUTH_RESEND_VERIFICATION_FAILED'
          });
          set({ error: errorMessage, isLoading: false });
          return { success: false, error: errorMessage };
        }
      },

      sendMagicLink: async (email: string) => {
        try {
          set({ isLoading: true, error: null });
          await sendMagicLink(email);
          set({ isLoading: false });
          return { success: true };
        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to send magic link';
          reportClientError({
            message: errorMessage,
            errorName: error instanceof Error ? error.name : 'AuthError',
            stack: error instanceof Error ? error.stack : undefined,
            screen: 'login',
            code: 'AUTH_MAGIC_LINK_FAILED'
          });
          set({ error: errorMessage, isLoading: false });
          return { success: false, error: errorMessage };
        }
      },

      completeMagicLinkSignIn: async (email?: string) => {
        try {
          // Supabase handles this via hash fragment automatically usually
          // But if we need explicit handling:
          set({ isLoading: true });
          const { session } = await getSession();
          if (session?.user) {
            const jwtUser = mapSupabaseUserWithJWT(session.user, session.access_token);
            const resolvedRole = await resolveRoleWithBackendFallback(session.access_token, jwtUser.role);
            const user = { ...jwtUser, role: resolvedRole };
            get().setUser(user, session.access_token);
            get().setToken(session.access_token);
            set({ isLoading: false });
            return { success: true, user };
          }
          set({ isLoading: false });
          return { success: false, error: 'No valid session found' };
        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : 'Magic link sign in failed';
          set({ error: errorMessage, isLoading: false });
          return { success: false, error: errorMessage };
        }
      },

      getIdToken: async () => {
        const { data } = await supabase.auth.getSession();
        return data.session?.access_token || null;
      },

      checkAuthStatus: async () => {
        await get().initializeAuth();
        return get().isAuthenticated;
      },

      requestAccess: async (data: AccessRequestData) => {
        try {
          set({ isLoading: true, error: null });

          // Determine the name to use (prefer provided name, then user name, then email prefix)
          const name = data.name || get().user?.name || data.email.split('@')[0] || 'User';

          // Submit platform access request to the backend API
          const response = await api.post<{ id: number; status: string; message: string }>('/api/access-requests', {
            email: data.email,
            message: data.message || '',
            name
          }, { skipAuth: true }); // Public endpoint, no auth required

          if (!response.success) {
            throw new Error(response.error?.message || 'Failed to submit access request');
          }

          set({ isLoading: false });
          return { success: true, data: response.data };
        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to submit access request';
          set({ error: errorMessage, isLoading: false });
          return { success: false, error: errorMessage };
        }
      },

      initializeAuth: async () => {
        if (initializeAuthInFlight) {
          return initializeAuthInFlight;
        }

        const initPromise = (async () => {
          console.log('[Auth Store] Initializing Supabase Auth...');
          set({ isLoading: true });

          // Read Supabase session directly from localStorage to avoid navigator.locks
          // deadlock in Supabase JS v2.90+ (the lock-based getSession() can hang forever
          // in certain browser contexts — reading storage directly is safe for initialization).
          let session: import('@supabase/supabase-js').Session | null = null;
          try {
            const storageKey = Object.keys(localStorage).find(
              k => k.startsWith('sb-') && k.endsWith('auth-token')
            );
            if (storageKey) {
              const raw = localStorage.getItem(storageKey);
              if (raw) {
                const parsed = JSON.parse(raw) as { access_token?: string; refresh_token?: string; user?: import('@supabase/supabase-js').User; expires_at?: number };
                if (parsed?.access_token && parsed?.user) {
                  session = parsed as unknown as import('@supabase/supabase-js').Session;
                }
              }
            }
          } catch {
            // Fall back to null session — will sign out / redirect to login
            session = null;
          }

          if (session?.user) {
            const token = session.access_token;

            // Check if token is expired
            if (isJWTExpired(token)) {
              console.warn('[Auth Store] JWT token is expired, signing out');
              await signOut();
              set({
                user: null,
                token: null,
                isAuthenticated: false,
                isInitialized: true,
                isLoading: false
              });
              get().setToken(null);
            } else {
              // Decode JWT to get user data with role from app_metadata
              const jwtUser = mapSupabaseUserWithJWT(session.user, token);
              const resolvedRole = await resolveRoleWithBackendFallback(token, jwtUser.role);

              // Fetch full user profile from backend to ensure identity consistency
              let dbUser = null;
              try {
                const profileResponse = await api.get<AuthMeData>('/api/auth/me');
                if (profileResponse.success && profileResponse.data) {
                  if (profileResponse.data.user) {
                    dbUser = profileResponse.data.user;
                  }
                }
              } catch (profileError) {
                console.warn('[Auth Store] Failed to fetch user profile, using JWT data:', profileError);
              }

              // Merge JWT data with database profile if available
              const user = dbUser ? {
                ...jwtUser,
                ...(dbUser as Partial<typeof jwtUser>),
                role: resolvedRole
              } : { ...jwtUser, role: resolvedRole };

              console.log('[Auth Store] User authenticated with resolved role:', user.role, {
                hasDbProfile: !!dbUser
              });


              set({
                user,
                token,
                isAuthenticated: true,
                isInitialized: true,
                isLoading: false
              });
              get().setToken(token);
            }
          } else {
            console.log('[Auth Store] Auth state change - no session, user signed out');
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              isInitialized: true,
              isLoading: false
            });
            get().setToken(null);
          }

          // Attach listener once to prevent subscription loops.
          if (!authSubscription) {
            const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
              console.log(`[Supabase Auth] ${event}`, session?.user?.email || 'no user');

              if (session?.user) {
                const token = session.access_token;

                // Check if token is expired
                if (isJWTExpired(token)) {
                  console.warn('[Auth Store] JWT token expired during auth state change, signing out');
                  await signOut();
                  set({
                    user: null,
                    token: null,
                    isAuthenticated: false,
                    isInitialized: true,
                    isLoading: false,
                  });
                  get().setToken(null);
                  return;
                }

                // Decode JWT to get user data with role from app_metadata
                const jwtUser = mapSupabaseUserWithJWT(session.user, token);
                const resolvedRole = await resolveRoleWithBackendFallback(token, jwtUser.role);
                const user = { ...jwtUser, role: resolvedRole };
                console.log(`[Auth Store] Auth state change - user resolved role:`, user.role);

                set({
                  user,
                  token,
                  isAuthenticated: true,
                  isInitialized: true,
                  isLoading: false,
                });
                get().setToken(token);

              } else {
                set({
                  user: null,
                  token: null,
                  isAuthenticated: false,
                  isInitialized: true,
                  isLoading: false,
                });
                get().setToken(null);
              }
            });

            authSubscription = data.subscription;
          }
        })().finally(() => {
          initializeAuthInFlight = null;
        });
        initializeAuthInFlight = initPromise;

        return initPromise;
      },

      completeRegistrationProfile: async (profileData: {
        userType: 'dc_member' | 'visitor';
        firstName: string;
        surname: string;
        phoneNumber: string;
        country: string;
        dcMembershipId?: string;
        ministryName?: string;
        ministryState?: string;
        ministryCountry?: string;
      }) => {
        try {
          set({ isLoading: true, error: null });

          interface ProfileUpdateResponse {
            user: User;
          }
          const response = await api.put<ProfileUpdateResponse>('/api/users/me', profileData);

          if (!response.success) {
            throw new Error(response.error?.message || 'Failed to update profile');
          }

          // Update local user state with the returned user data
          if (response.data?.user) {
            const currentUser = get().user;
            if (currentUser) {
              set({
                user: {
                  ...currentUser,
                  ...response.data.user
                }
              });
            }
          }

          set({ isLoading: false });
          return response.data;
        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : 'Profile update failed';
          set({
            error: errorMessage,
            isLoading: false
          });
          throw error;
        }
      },

      refreshRole: async () => {
        try {
          const currentUser = get().user;
          if (!currentUser) {
            return { roleChanged: false, newRole: null };
          }

          const response = await api.get<AuthRoleData>('/api/auth/role');
          if (response.success && response.data) {
            const roleData = response.data;
            const normalizedRole = typeof roleData.role === 'string'
              ? {
                id: (() => {
                  try {
                    return getRoleId(roleData.role);
                  } catch {
                    return currentUser.role.id;
                  }
                })(),
                name: roleData.role
              }
              : roleData.role;

            if (roleData.roleChanged) {
              console.log('[Auth Store] Role changed from', roleData.previousRole, 'to', normalizedRole.name);

              const updatedUser = {
                ...currentUser,
                role: { id: normalizedRole.id, name: normalizedRole.name as User['role']['name'] }
              };

              set({ user: updatedUser });

              return { roleChanged: true, newRole: normalizedRole.name };
            }

            return { roleChanged: false, newRole: normalizedRole.name };
          }

          return { roleChanged: false, newRole: null };
        } catch (error) {
          console.warn('[Auth Store] Failed to refresh role:', error);
          return { roleChanged: false, newRole: null };
        }
      },

    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ token: state.token }), // Only persist token, not user data
    }
  )
);
